import { apiClient } from './api.client';
import { API_CONFIG } from '../config/api.config';
import { VisitorLog, ApiResponse } from '../types';
import { visitorLogSavePayload } from '../utils/visitorMedia';

export interface VisitorLogFilters {
  page?: number;
  size?: number;
  filter?: string;
  sort?: string;
}

export interface CreateVisitorLogData {
  location_id: number;
  log_capture_time: string;
  log_access_type: 'ENTRY' | 'EXIT';
  log_operator_type: 'DRIVER' | 'VISITOR';
  log_license_plate: string;
  log_vehicle_color: string;
  log_vehicle_model: string;
  log_license_image?: string;
  log_image?: string;
  log_url?: string;
  vehicle_details?: string;
}

export interface UpdateVisitorLogData extends Partial<CreateVisitorLogData> {}

export interface ImageUploadResponse {
  success: boolean;
  url: string;
  filename: string;
  size_bytes: number;
  log_type: string;
}

class VisitorLogService {
  /**
   * Get all visitor logs with optional filters
   */
  async getVisitorLogs(filters?: VisitorLogFilters): Promise<ApiResponse<VisitorLog[]>> {
    return apiClient.get<ApiResponse<VisitorLog[]>>(
      API_CONFIG.ENDPOINTS.VISITOR_LOGS,
      filters
    );
  }

  /**
   * Get visitor log by ID
   */
  async getVisitorLogById(id: string | number): Promise<VisitorLog> {
    return apiClient.get<VisitorLog>(`${API_CONFIG.ENDPOINTS.VISITOR_LOG}/${id}`);
  }

  /**
   * Get visitor logs by location
   */
  async getVisitorLogsByLocation(locationId: number): Promise<ApiResponse<VisitorLog[]>> {
    return this.getVisitorLogs({
      filter: `location_id::${locationId}`,
    });
  }

  /**
   * Get entry logs only
   */
  async getEntryLogs(): Promise<ApiResponse<VisitorLog[]>> {
    return this.getVisitorLogs({
      filter: 'log_access_type::ENTRY',
    });
  }

  /**
   * Get exit logs only
   */
  async getExitLogs(): Promise<ApiResponse<VisitorLog[]>> {
    return this.getVisitorLogs({
      filter: 'log_access_type::EXIT',
    });
  }

  /**
   * Get driver logs
   */
  async getDriverLogs(): Promise<ApiResponse<VisitorLog[]>> {
    return this.getVisitorLogs({
      filter: 'log_operator_type::DRIVER',
    });
  }

  /**
   * Get visitor logs by date range
   */
  async getVisitorLogsByDateRange(startDate: string, endDate: string): Promise<ApiResponse<VisitorLog[]>> {
    return this.getVisitorLogs({
      filter: `log_capture_time_gte::${startDate},log_capture_time_lte::${endDate}`,
    });
  }

  /**
   * Search by license plate
   */
  async searchByLicensePlate(plate: string): Promise<ApiResponse<VisitorLog[]>> {
    return this.getVisitorLogs({
      filter: `log_license_plate:like::${plate}`,
    });
  }

  /**
   * Upload image to S3 and get URL
   */
  async uploadImage(
    file: File, 
    logType: 'id' | 'face' | 'license_plate' | 'vehicle_front' | 'vehicle_right' | 'vehicle_left' | 'vehicle_back',
    locationId?: number
  ): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    const params: Record<string, string> = { log_type: logType };
    if (locationId) {
      params.location_id = locationId.toString();
    }
    
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `${API_CONFIG.ENDPOINTS.VISITOR_LOG_UPLOAD_IMAGE}?${queryString}`;
    
    return apiClient.post<ImageUploadResponse>(endpoint, formData);
  }

  /**
   * Create new visitor log
   */
  async createVisitorLog(data: CreateVisitorLogData | FormData): Promise<VisitorLog> {
    return apiClient.post<VisitorLog>(API_CONFIG.ENDPOINTS.VISITOR_LOGS, visitorLogSavePayload(data));
  }

  /**
   * Update visitor log
   */
  async updateVisitorLog(id: string | number, data: UpdateVisitorLogData | FormData): Promise<VisitorLog> {
    return apiClient.put<VisitorLog>(`${API_CONFIG.ENDPOINTS.VISITOR_LOG}/${id}`, visitorLogSavePayload(data));
  }

  /**
   * Delete visitor log
   */
  async deleteVisitorLog(id: string | number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`${API_CONFIG.ENDPOINTS.VISITOR_LOG}/${id}`);
  }
}

export const visitorLogService = new VisitorLogService();
